import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export interface BreadcrumbData {
  label: string;
  route: string;
  queryParams?: Params;
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private readonly dynamicBreadcrumbs = new BehaviorSubject<BreadcrumbData[]>([]);

  // Getter to access current breadcrumb data synchronously
  get getCurrentBreadcrumbs(): BreadcrumbData[] {
    return this.dynamicBreadcrumbs.value;
  }

  // Method to set dynamic breadcrumb for a specific route
  setDynamicBreadcrumb(route: string, label: string, queryParams?: Params) {
    const currentBreadcrumbs = this.dynamicBreadcrumbs.value;
    const existingIndex = currentBreadcrumbs.findIndex(bc => bc.route === route);

    if (existingIndex >= 0) {
      currentBreadcrumbs[existingIndex].label = label;
    } else {
      currentBreadcrumbs.push({ route, label, queryParams });
    }
    this.dynamicBreadcrumbs.next([...currentBreadcrumbs]);
  }

  setNestedDynamicBreadcrumbs(baseRoute: string, userId: string, userName: string, queryParams?: Params, subroute?: string, employeeId?: string, employeeName?: string): void {
    this.setDynamicBreadcrumb(`${baseRoute}/${userId}`, userName, queryParams);

    this.setDynamicBreadcrumb(`${baseRoute}/${userId}/edit`, subroute ?? 'Edit User');

    this.setDynamicBreadcrumb(`${baseRoute}/${userId}/tasks/create`, 'Assign a New Task');
    this.setDynamicBreadcrumb(`${baseRoute}/${userId}/tasks`, 'Tasks');

    this.setDynamicBreadcrumb(`${baseRoute}/${userId}/notes`, 'Notes');

    this.setDynamicBreadcrumb(`${baseRoute}/${userId}/documents`, 'Documents');

    this.setDynamicBreadcrumb(`${baseRoute}/${userId}/employees`, 'Employees');
    if (employeeName) {
      this.setDynamicBreadcrumb(`${baseRoute}/${userId}/employees/${employeeId}`, employeeName);
    }
  }

  // Method to get dynamic breadcrumb for a specific route
  getDynamicBreadcrumb(route: string): string | null {
    const breadcrumb = this.dynamicBreadcrumbs.value.find(bc => bc.route === route);

    if (breadcrumb) {
      return breadcrumb.label;
    }

    const routeSegments = route.split('/');
    const matchingBreadcrumb = this.dynamicBreadcrumbs.value.find(bc => {
      const bcSegments = bc.route.split('/');
      if (routeSegments.length !== bcSegments.length) {
        return false;
      }

      for (let i = 0; i < routeSegments.length; i++) {
        if (bcSegments[i] !== routeSegments[i] && !bcSegments[i].startsWith(':')) {
          return false;
        }
      }
      return true;
    });

    if (matchingBreadcrumb) {
      return matchingBreadcrumb.label;
    }

    return null;
  }

  clearDynamicBreadcrumbs(): void {
    this.dynamicBreadcrumbs.next([]);
  }

  get dynamicBreadcrumbs$() {
    return this.dynamicBreadcrumbs.asObservable();
  }
} 